import {
	ChevronDown,
	Loader2,
	SlidersHorizontal
} from "lucide-react";
import * as React from "react";

import type {
	ColumnDef,
	ColumnFiltersState,
	PaginationState,
	SortingState,
	VisibilityState
} from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,

	DropdownMenuContent,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from "@/components/ui/table";
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable
} from "@tanstack/react-table";

import { ExpandingSearchBar } from "../dashboard/search-bar";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { dateRangeFilter, fuzzyFilter } from "./filters";

interface DataTableProps<TData> {
	data: TData[];
	title: string;
	isError?: boolean;
	isLoading?: boolean;
	pagination?: PaginationState;
	columns: ColumnDef<TData>[];
	customLabels?: Record<string, string>;
}
export function DataTable<TData>({
	title,
	data,
	columns,
	isError,
	isLoading
}: DataTableProps<TData>) {
	const [sorting, setSorting] = React.useState<SortingState>([]);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		[]
	);

	const [columnVisibility, setColumnVisibility] = React.useState<
		VisibilityState
	>({});
	const [rowSelection, setRowSelection] = React.useState({});
	const [pageIndex, setPageIndex] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(20);

	const table = useReactTable({
		data,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		filterFns: {
			fuzzy: fuzzyFilter,
			dateRange: dateRangeFilter
		},
		globalFilterFn: fuzzyFilter,
		state: {
			sorting,
			globalFilter,
			columnFilters,
			columnVisibility,
			rowSelection,
			pagination: {
				pageIndex,
				pageSize
			}
		},
		onPaginationChange: (updater) => {
			if (typeof updater === "function") {
				const newState = updater({
					pageIndex,
					pageSize
				});
				setPageIndex(newState.pageIndex);
				setPageSize(newState.pageSize);
			}
		}
	});
	// const isMobile = useIsMobile();

	return (
		<div className="w-full bg-white p-2 rounded-lg">
			<div className="flex items-center py-4 gap-2 max-md:flex-wrap">
				<h3>{title}</h3>

				<ExpandingSearchBar
					placeholder="Search orders..."
					onChange={event => setGlobalFilter(event.target.value)}
					className="ml-auto"
				/>

			</div>

			<div className="rounded-md border">
				<Table>
					<TableHeader className="max-md:hidden">
						{table.getHeaderGroups().map(headerGroup => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{isLoading
							? (
									<TableRow>
										<TableCell
											colSpan={columns.length}
											className="h-24 text-center "
										>
											<span className="flex items-center justify-center opacity-50 m-auto">
												<Loader2 size={20} className="animate-spin" />
												{" "}
												&nbsp;
												Loading....
											</span>
										</TableCell>
									</TableRow>
								)
							: table.getRowModel().rows?.length
								? (
										table.getRowModel().rows.map(row => (
											<TableRow
												key={row.id}
												className="max-sm:flex flex-col"
												data-state={row.getIsSelected() && "selected"}
											>
												{row.getVisibleCells().map(cell => (
													<TableCell
														key={cell.id}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</TableCell>
												))}
											</TableRow>
										))
									)
								: (
										<TableRow>
											<TableCell
												colSpan={columns.length}
												className="h-24 text-center"
											>
												{isError ? "Error Fetching Data" : "No results."}
											</TableCell>
										</TableRow>
									)}
					</TableBody>
				</Table>
			</div>

			<div className="flex items-center justify-end space-x-2 py-4">
				<div className="flex-1 text-sm text-muted-foreground">
					{table.getFilteredSelectedRowModel().rows.length}
					{" "}
					of
					{" "}
					{table.getFilteredRowModel().rows.length}
					{" "}
					row(s) selected.
				</div>
				<div className="space-x-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => table.previousPage()}
						disabled={!table.getCanPreviousPage()}
					>
						Previous
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => table.nextPage()}
						disabled={!table.getCanNextPage()}
					>
						Next
					</Button>
				</div>
			</div>
		</div>
	);
}

export function StatusFilterDropdown({ statuses }: { statuses: string[] }) {
	const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);
	const [open, setOpen] = React.useState(false);

	const handleStatusChange = (statusId: string) => {
		setSelectedStatuses(prev =>
			prev.includes(statusId)
				? prev.filter(id => id !== statusId)
				: [...prev, statusId]
		);
	};

	const handleApply = () => {
		setOpen(false);
	};

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" className="w-[180px] justify-between">
					<SlidersHorizontal className="h-4 w-4 opacity-50" />
					Filter by Status
					<ChevronDown className="h-4 w-4 opacity-50" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-[180px] p-0" align="start">
				<Card className="border-0 shadow-none">
					<div className="space-y-5 p-4">
						<div className="space-y-3">
							{statuses.map(status => (
								<div
									key={status}
									className="flex items-center space-x-2 rounded-md"
								>
									<Checkbox
										id={status}
										checked={selectedStatuses.includes(status)}
										onCheckedChange={() => handleStatusChange(status)}
										className="border-gray-400"
									/>
									<Label
										htmlFor={status}
										className="text-sm font-normal capitalize leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
									>
										{status}
									</Label>
								</div>
							))}
						</div>
						<div className="flex space-x-2">
							<Button className="flex-1" onClick={handleApply}>
								Apply
							</Button>
							<Button
								variant="outline"
								className="flex-1"
								onClick={() => setSelectedStatuses([])}
							>
								Reset
							</Button>
						</div>
					</div>
				</Card>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
